/**
 * Pure React Query hooks for user credentials
 *
 * Simple, clean approach for API keys and OAuth tokens
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../stores/authStore'
import { queryKeys } from '../queryKeys'

// User credentials interface
export interface UserCredentials {
  apiKey?: string
}

const defaultCredentials: UserCredentials = {}

// Pure React Query hook for user credentials
export function useUserCredentials() {
  const { user } = useAuth()

  return useQuery({
    queryKey: queryKeys.userCredentials(user?.id || ''),
    queryFn: async (): Promise<UserCredentials> => {
      if (!user) {
        throw new Error('No authenticated user')
      }

      const { data, error } = await supabase
        .from('user_credentials')
        .select('*')
        .eq('user_id', user.id)
        .single()

      if (error && error.code !== 'PGRST116') {
        console.error('Failed to fetch user credentials:', error)
        throw error
      }

      if (!data) return defaultCredentials

      return {
        apiKey:
          data.hevy_api_key && data.hevy_api_key !== 'CSV_IMPORT' ? data.hevy_api_key : undefined
      }
    },
    enabled: !!user
  })
}

// Simple mutation for updating credentials with optimistic updates
export function useUpdateCredentials() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (updates: { hevy_api_key?: string | null }): Promise<void> => {
      if (!user) throw new Error('No authenticated user')

      const { error } = await supabase.from('user_credentials').upsert({
        user_id: user.id,
        ...updates,
        updated_at: new Date().toISOString()
      })

      if (error) {
        console.error('Failed to update user credentials:', error)
        throw error
      }
    },
    onMutate: async (updates) => {
      const credentialsKey = queryKeys.userCredentials(user?.id || '')

      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: credentialsKey })

      // Snapshot the current value
      const previousCredentials = queryClient.getQueryData<UserCredentials>(credentialsKey)

      // Optimistically update the cache
      queryClient.setQueryData(credentialsKey, (old: UserCredentials | undefined) => {
        const current = old || defaultCredentials
        return {
          ...current,
          apiKey:
            updates.hevy_api_key !== undefined ? updates.hevy_api_key || undefined : current.apiKey
        }
      })

      // Return rollback data
      return { previousCredentials }
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousCredentials) {
        queryClient.setQueryData(
          queryKeys.userCredentials(user?.id || ''),
          context.previousCredentials
        )
      }
      console.error('Failed to save credentials:', error)
      toast.error('Failed to save credentials')
    },
    onSettled: () => {
      // Optimistic updates handle the cache, no need to invalidate
    }
  })
}

// Convenience hooks for specific credentials
export function useUpdateApiKey() {
  const updateCredentials = useUpdateCredentials()
  return useMutation({
    mutationFn: (apiKey: string) => updateCredentials.mutateAsync({ hevy_api_key: apiKey })
  })
}

export function useClearApiKey() {
  const updateCredentials = useUpdateCredentials()
  return useMutation({
    mutationFn: () => updateCredentials.mutateAsync({ hevy_api_key: null })
  })
}
